import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
} from 'react-share';

import { Loading } from './Loading';
import {
  PageContainer,
  PageHero,
  Section,
  Protected,
} from '../theme/primitives';
import { ShareGlyph, ShareBubble } from './ShareIcons';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const I = {
  Calendar: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  ChevronLeft: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m15 18-6-6 6-6" />
    </svg>
  ),
  ChevronRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m9 18 6-6-6-6" />
    </svg>
  ),
  Maximize: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  ),
};

/* ─────────── Carousel ─────────── */

function ArticleCarousel({ images, title, onZoom }) {
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const stripRef = useRef(null);

  const total = images.length;

  const goTo = useCallback((next) => {
    setDirection(next > idx ? 1 : -1);
    setIdx(((next % total) + total) % total);
  }, [idx, total]);

  const prev = () => goTo(idx - 1);
  const next = () => goTo(idx + 1);

  // Keep active thumb scrolled into view
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const active = strip.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [idx]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, total]);

  const slideTransform = direction > 0 ? 'translateX(2%)' : 'translateX(-2%)';

  return (
    <div className="esay-article-carousel">
      {/* Main viewer */}
      <div className="group relative overflow-hidden rounded-xl border border-border-subtle bg-black">
        {/* Soft blurred backdrop of the active image so letterbox bars feel intentional */}
        <img
          aria-hidden
          src={`/articles/${images[idx]}`}
          alt=""
          className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 transition-opacity duration-500 ease-out-soft"
        />
        <div className="relative aspect-[16/10]">
          {/* Render all images stacked, fade between active - contain to preserve full image */}
          {images.map((src, i) => (
            <img
              key={src + i}
              src={`/articles/${src}`}
              alt={i === 0 ? title : ''}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`
                absolute inset-0 h-full w-full object-contain
                transition-[opacity,transform] duration-[600ms] ease-out-soft
                ${i === idx ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.02] pointer-events-none'}
              `}
              style={i === idx ? undefined : { transform: slideTransform }}
            />
          ))}

          {/* Tap target to open lightbox */}
          <button
            type="button"
            onClick={() => onZoom(idx)}
            aria-label="Open in lightbox"
            className="absolute inset-0 cursor-zoom-in"
          />

          {/* Top gradient for badges */}
          <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/55 to-transparent" />

          {/* Counter */}
          {total > 1 && (
            <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/55 backdrop-blur-md px-2.5 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
              <span className="text-amber">{String(idx + 1).padStart(2, '0')}</span>
              <span className="text-white/40">/</span>
              <span>{String(total).padStart(2, '0')}</span>
            </div>
          )}

          {/* Zoom hint */}
          <button
            type="button"
            onClick={() => onZoom(idx)}
            aria-label="Open in lightbox"
            className="
              absolute top-3 right-3
              inline-flex h-9 w-9 items-center justify-center rounded-full
              border border-white/15 bg-black/55 backdrop-blur-md
              text-white
              opacity-0 translate-y-[-4px]
              transition-[opacity,transform,background-color] duration-300 ease-out-soft
              group-hover:opacity-100 group-hover:translate-y-0
              hover:bg-amber hover:border-amber hover:text-ink
            "
          >
            <I.Maximize className="h-4 w-4" />
          </button>

          {/* Prev / Next - hidden until hover, no clutter at rest */}
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous image"
                className="
                  absolute left-3 top-1/2 -translate-y-1/2
                  inline-flex h-10 w-10 items-center justify-center rounded-full
                  border border-white/15 bg-black/55 backdrop-blur-md text-white
                  opacity-0 -translate-x-1
                  transition-[opacity,transform,background-color,color] duration-300 ease-out-soft
                  group-hover:opacity-100 group-hover:translate-x-0
                  hover:bg-amber hover:border-amber hover:text-ink
                "
              >
                <I.ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next image"
                className="
                  absolute right-3 top-1/2 -translate-y-1/2
                  inline-flex h-10 w-10 items-center justify-center rounded-full
                  border border-white/15 bg-black/55 backdrop-blur-md text-white
                  opacity-0 translate-x-1
                  transition-[opacity,transform,background-color,color] duration-300 ease-out-soft
                  group-hover:opacity-100 group-hover:translate-x-0
                  hover:bg-amber hover:border-amber hover:text-ink
                "
              >
                <I.ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Progress dots */}
          {total > 1 && total <= 10 && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {images.map((_, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={`block h-[3px] rounded-full transition-[width,background-color] duration-400 ease-out-soft ${
                    i === idx ? 'w-6 bg-amber' : 'w-2 bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail filmstrip */}
      {total > 1 && (
        <div
          ref={stripRef}
          className="
            mt-2.5 -mx-1 px-1
            flex items-center gap-1.5 overflow-x-auto
            scrollbar-thin
          "
          style={{ scrollbarWidth: 'thin' }}
        >
          {images.map((img, i) => {
            const active = i === idx;
            return (
              <button
                key={img + i}
                type="button"
                onClick={() => goTo(i)}
                data-active={active}
                aria-label={`Show image ${i + 1}`}
                className={`
                  relative shrink-0 overflow-hidden rounded-md
                  h-14 sm:h-16 aspect-[4/3]
                  bg-surface-overlay
                  transition-[transform,box-shadow,border-color] duration-300 ease-out-soft
                  ${active
                    ? 'border-2 border-amber shadow-[0_4px_14px_-6px_rgba(251,191,36,0.6)]'
                    : 'border border-border-subtle hover:border-amber/40 hover:-translate-y-0.5'}
                `}
              >
                <img
                  src={`/articles/${img}`}
                  alt=""
                  loading="lazy"
                  className={`h-full w-full object-cover transition-opacity duration-300 ease-out-soft ${active ? 'opacity-100' : 'opacity-75 hover:opacity-100'}`}
                />
                {active && (
                  <span aria-hidden className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const SingleAwardAndNew = () => {
  const { id } = useParams();
  const url = window.location.href;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Axios.post('/new_apis/get-single-article', { id }, { headers: { key } }).then((res) => {
      setData(res.data.article_data || null);
      setMetadata(res.data.seo_data?.[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const openLightbox = useCallback((startIndex) => {
    const imgs = (data?.article_images || '').split(',').filter(Boolean);
    const start = Math.max(0, Math.min(startIndex || 0, imgs.length - 1));
    const rotated = start === 0 ? imgs : [...imgs.slice(start), ...imgs.slice(0, start)];
    setLightboxImages(rotated);
    setLightboxOpen(true);
  }, [data]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loading />;
  if (!data) return null;

  const images = (data.article_images || '').split(',').filter(Boolean);

  return (
    <Protected>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <PageHero compact kicker="Honors & Highlights" title="Awards & News" />

        <Section>
          <article className="
            relative overflow-hidden rounded-2xl
            border border-border-subtle bg-surface-paper
            p-4 sm:p-6 md:p-8
          ">
            <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

            {/* Date pill */}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
              <I.Calendar className="h-3 w-3 text-amber" />
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{data.article_published_date}</span>
            </div>

            {/* Title */}
            <h1 className="mt-3 text-[22px] sm:text-[28px] md:text-[32px] font-bold leading-[1.18] tracking-[-0.022em] text-fg-primary">
              {data.article_title}
            </h1>

            {/* Amber accent line */}
            <div aria-hidden className="mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />

            {/* Image carousel */}
            {images.length > 0 && (
              <div className="mt-4">
                <ArticleCarousel
                  images={images}
                  title={data.article_title}
                  onZoom={openLightbox}
                />
              </div>
            )}

            {/* Share row */}
            <div className="mt-6 pt-4 border-t border-border-subtle flex flex-wrap items-center gap-2">
              <span className="mr-2 text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Share</span>
              <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
              <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
              <TwitterShareButton url={url}><ShareBubble label="X" size="sm"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
              <RedditShareButton url={url}><ShareBubble label="Reddit" size="sm"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
              <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
              <TelegramShareButton url={url}><ShareBubble label="Telegram" size="sm"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
              <EmailShareButton url={url}><ShareBubble label="Email" size="sm"><ShareGlyph.Email /></ShareBubble></EmailShareButton>

              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? 'Link copied' : 'Copy link'}
                title={copied ? 'Link copied!' : 'Click to copy the URL'}
                className={`
                  inline-flex h-9 w-9 items-center justify-center rounded-full
                  border bg-surface-elevated cursor-pointer
                  transition-[background-color,border-color,color,transform,box-shadow] duration-200 ease-out-soft
                  hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                  hover:shadow-[0_8px_18px_-6px_rgba(251,191,36,0.55)]
                  ${copied ? 'border-amber text-amber' : 'border-border-subtle text-fg-secondary'}
                `}
              >
                {copied ? <I.Check className="h-4 w-4" /> : <I.Link className="h-4 w-4" />}
              </button>
            </div>

            {/* Body */}
            <div
              className="
                mt-5 rounded-xl border p-4 sm:p-5
                text-[14px] leading-[1.7]
                [&_p]:mb-3
                [&_a]:text-amber-deep [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-amber
                [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-3
                [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[19px] [&_h1]:font-bold
                [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[17px] [&_h2]:font-bold
                [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-[15px] [&_h3]:font-bold
                [&_strong]:font-bold
                [&_b]:font-bold
                [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-2
                [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-2
                [&_li]:my-0.5
              "
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(10,10,11,0.08)',
                color: '#1f1f23',
                boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
              }}
            >
              {parse(data.article_desc || '')}
            </div>
          </article>
        </Section>

        {lightboxOpen && (
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={lightboxImages.map((src) => ({ src: `/articles/${src}` }))}
          />
        )}
      </PageContainer>
    </Protected>
  );
};
